// 2026-06-18 - FormuLearning - Step 1 검색 이력(스냅샷) UI
// 2026-11-03 수정 - 이슈 #114: viewer 는 검색 이력 «복원» 비활성(목록 펼쳐 보기는 허용).
// 파일 위치: frontend/src/features/step1/SearchHistory.tsx
// 정보/뷰 분리(이슈 #6): 검색 1회당 1건 보존되는 불변 스냅샷 목록 + 과거 시점 복원.
// 2026-09-17 (이슈 #79): 검색 중에는 복원을 잠근다 — 진행 중 검색이 복원한 뷰 위에 저장된다.

import { useState } from 'react'
import type { SnapshotListItem } from '../../api/step1'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

interface Props {
  snapshots: SnapshotListItem[]
  currentSnapshotId: string | null
  onRestore: (id: string) => void
  restoringId?: string | null
  /** 검색 진행 중 잠금(#79) — 목록 열람은 되고 복원만 막는다 */
  disabled?: boolean
}

function fmtTime(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

export default function SearchHistory({ snapshots, currentSnapshotId, onRestore, restoringId, disabled }: Props) {
  const canWrite = useCanWrite() // viewer(#114) — 이력 펼쳐 보기는 허용, 복원은 비활성
  const [open, setOpen] = useState(false)

  if (snapshots.length === 0) return null

  return (
    <div className="mb-4">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-[11px] font-medium text-text-secondary transition-opacity hover:opacity-80"
      >
        <span>🕘 검색 이력 ({snapshots.length})</span>
        <span className="text-[9px]">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="panel-in mt-2 space-y-1.5">
          {snapshots.map((s) => {
            const current = s.id === currentSnapshotId
            const restoring = s.id === restoringId
            return (
              <div
                key={s.id}
                className="flex items-center justify-between gap-2 rounded-md border px-3 py-2"
                style={{
                  borderColor: current ? 'var(--border-purple)' : 'var(--border-subtle)',
                  background: current ? 'var(--bg-row-active)' : 'transparent',
                }}
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] font-semibold text-text-primary">{s.search_input || '—'}</span>
                    {s.resolved_en && (
                      <span className="rounded px-1.5 py-0.5 text-[9px]" style={{ background: 'var(--color-bg-panel)' }}>EN · {s.resolved_en}</span>
                    )}
                    {s.resolved_ko && (
                      <span className="rounded px-1.5 py-0.5 text-[9px]" style={{ background: 'var(--color-bg-panel)' }}>KO · {s.resolved_ko}</span>
                    )}
                    {current && (
                      <span className="rounded-full px-2 py-0.5 text-[9px] font-medium" style={{ background: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' }}>현재</span>
                    )}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[9px] text-text-secondary">
                    <span>{fmtTime(s.fetched_at)}</span>
                    {(s.sources ?? []).length > 0 && <span className="text-text-muted">· {(s.sources ?? []).join(', ')}</span>}
                  </div>
                </div>
                {!current && (
                  <button
                    onClick={() => onRestore(s.id)}
                    disabled={restoring || disabled || !canWrite}
                    title={canWrite ? undefined : READ_ONLY_TITLE}
                    className="shrink-0 rounded px-2 py-1 text-[10px] font-medium transition-opacity hover:opacity-80 disabled:opacity-50"
                    style={{ background: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' }}
                  >
                    {restoring ? '복원 중…' : '복원'}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
